import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { take } from 'rxjs/operators';
import {
  LogMiddlewareServiceAttributes,
  LogMiddlewareServiceDetailAttributes,
} from '@albatrosdeveloper/ave-models-package';
import { SmartGridComponent } from 'app/shared/components/smart-grid/public-api';
import {
  ResponsiveModalHeaderComponent,
  ResponsiveModalContentComponent,
} from 'app/shared/components/responsive-modal';
import { ResponsiveDialogService } from 'app/shared/services/responsive-dialog.service';
import { SystemLatencyService } from '../../system-latency/system-latency.service';
import {
  IntegrationErrorJsonViewerModalComponent,
  IntegrationErrorJsonViewerData,
} from './integration-error-json-viewer-modal.component';
import { SpanishPaginatorIntl } from './spanish-paginator-intl';
import {
  buildIntegrationErrorDetailColumnDefs,
  INTEGRATION_ERROR_DETAIL_GRID_CONFIG,
} from './integration-errors-modal.constants';

/**
 * Detalle por registro de un error de integración: los renglones de
 * `log_middleware_service_detail` del log seleccionado, con las mismas
 * columnas y renderers que el modal de logs de Latencia — la data enviada y
 * recibida son botones que abren el visor de JSON (con copiar al
 * portapapeles). Carga una vez y la grilla en modo local da búsqueda, orden,
 * paginación y filtros por columna.
 */
@Component({
  selector: 'app-integration-error-detail-modal',
  standalone: true,
  imports: [
    CommonModule,
    SmartGridComponent,
    ResponsiveModalHeaderComponent,
    ResponsiveModalContentComponent,
  ],
  providers: [{ provide: MatPaginatorIntl, useClass: SpanishPaginatorIntl }],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <app-responsive-modal-header
      title="Detalle del error de integración"
      [subtitle]="subtitle"
      icon="sync_problem"
      iconColor="red"
    />

    <app-responsive-modal-content padding="p-3" [scrollable]="false">
      <app-smart-grid
        class="flex-1 min-h-0"
        [data]="rows()"
        [columnDefs]="columnDefs"
        [config]="gridConfig"
        [loading]="isLoading()"
      />
    </app-responsive-modal-content>
  `,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
      }

      /* Cadena de altura completa: host → smart-grid → wrapper → container,
         para que la grilla llene el modal y el paginador (mt-auto) quede
         siempre al pie sin espacio muerto debajo. */
      :host ::ng-deep app-smart-grid {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
      }

      :host ::ng-deep app-smart-grid .smart-grid-wrapper {
        flex: 1;
        min-height: 0;
      }

      :host ::ng-deep app-smart-grid .smart-grid-container {
        flex: 1;
        min-height: 0;
      }
    `,
  ],
})
export class IntegrationErrorDetailModalComponent implements OnInit {
  readonly log: LogMiddlewareServiceAttributes = inject(MAT_DIALOG_DATA);
  private readonly latencyService = inject(SystemLatencyService);
  private readonly responsiveDialog = inject(ResponsiveDialogService);

  readonly columnDefs = buildIntegrationErrorDetailColumnDefs({
    onOpenJson: (title, json) => this.openJsonViewer(title, json),
  });
  readonly gridConfig = INTEGRATION_ERROR_DETAIL_GRID_CONFIG;

  readonly rows = signal<LogMiddlewareServiceDetailAttributes[]>([]);
  readonly isLoading = signal<boolean>(true);

  get subtitle(): string {
    const code = this.log?.code ?? '';
    const name = this.log?.name ?? '';
    return code && name ? `${code} · ${name}` : code || name;
  }

  ngOnInit(): void {
    this.latencyService
      .findAllDetailWithCount({ currentPage: 1, limitPerPage: 100 }, String(this.log._id))
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          this.rows.set(res.data?.[0]?.data ?? []);
          this.isLoading.set(false);
        },
        error: () => {
          this.rows.set([]);
          this.isLoading.set(false);
        },
      });
  }

  private openJsonViewer(title: string, json: unknown): void {
    this.responsiveDialog.open<
      IntegrationErrorJsonViewerModalComponent,
      IntegrationErrorJsonViewerData
    >(IntegrationErrorJsonViewerModalComponent, {
      size: 'md',
      data: { title, json },
    });
  }
}
